<script setup lang="ts">
import type { SearchResponse } from '@algolia/client-search';
import { watchDebounced, watchThrottled } from '@vueuse/core';
import { ref } from 'vue';

import {
  AlgoliaCategory,
  AlgoliaVariant,
  CATEGORIES_INDEX,
  categorySearchParams,
  multiQuerySearch,
  PRODUCTS_INDEX,
  productSearchParams,
} from '@/api/algolia';
import DepartmentList from '@/components/layout/header/DepartmentList.vue';
import ProductList from '@/components/layout/header/ProductList.vue';
import { useFocusTrap } from '@/composables/useFocusTrap';
import { sendProductsSearched } from '@/utils/analytics/productSearched';

const props = defineProps<{
  query: string;
}>();

const emit = defineEmits<{
  (e: 'dismiss', setFocus?: boolean): void;
}>();

const isOpen = defineModel<boolean>({ required: true });

const featuredCategories = ref<SearchResponse<AlgoliaCategory>>();
const searchSuggestions = ref<AlgoliaVariant[]>([]);

const suggestionsModalRef = ref<HTMLElement>();

const handleClose = (dismissed?: boolean) => {
  isOpen.value = false;
  if (dismissed) emit('dismiss');
};

const fetchResults = async (search: string) => {
  const [categories, products] = await multiQuerySearch<[AlgoliaCategory, AlgoliaVariant]>(
    {
      indexName: CATEGORIES_INDEX,
      ...categorySearchParams({
        distinct: true,
        hitsPerPage: 5,
        filters: 'idealForSuggestions:true',
        query: search,
        ruleContexts: ['featuredCategories'],
      }),
    },
    {
      indexName: PRODUCTS_INDEX,
      ...productSearchParams({
        analytics: !!search,
        clickAnalytics: true,
        distinct: true,
        hitsPerPage: 5,
        filters: 'traits:searchable',
        query: search,
      }),
    },
  );
  featuredCategories.value = categories;
  searchSuggestions.value = products.hits;

  isOpen.value = true;
};

const { onNextElement, onPreviousElement } = useFocusTrap(suggestionsModalRef);
watchThrottled(() => props.query, fetchResults, { throttle: 200 });
watchDebounced(
  () => props.query,
  (query) => sendProductsSearched({ query, pageSection: 'Main Search Bar' }),
  { debounce: 400 },
);
</script>

<template>
  <div
    v-if="query && (featuredCategories?.hits.length || searchSuggestions.length)"
    v-show="isOpen"
    class="hidden absolute z-10 p-3 bg-white rounded-xl border border-solid xl:left-0 lg:-left-10 lg:-right-10 xl:right-0 border-neutral-300 lg:visible lg:block"
    id="search-suggestions"
    data-test="header-search-suggestions"
    ref="suggestionsModalRef"
    @keydown.esc.prevent="handleClose(true)"
    @keydown.exact.tab="onNextElement"
    @keydown.shift.tab="onPreviousElement"
  >
    <DepartmentList
      v-if="featuredCategories?.hits.length"
      :index="featuredCategories"
      @navigating="handleClose"
    />
    <ProductList
      v-if="searchSuggestions.length"
      :products="searchSuggestions"
      :query
      @navigating="handleClose"
    />
  </div>
</template>
